import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './NotificationPanel.css';

interface ReviewItem {
  id: string;
  name: string;
  path: string;
  folder_type: string;
  review_status: string;
  reviewer_email: string;
  submitted_by: string;
  submitted_at: string | null;
  reviewed_at: string | null;
}

export const NotificationPanel: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'in_review' | 'completed'>('in_review');
  const [inReview, setInReview] = useState<ReviewItem[]>([]);
  const [completed, setCompleted] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await apiClient.getMyReviews();
      setInReview(data.in_review);
      setCompleted(data.completed);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goToFolder = (item: ReviewItem) => {
    const page = item.folder_type === 'test_scenario' ? 'test-scenarios' : 'test-cases';
    navigate(`/${page}?folder_id=${item.id}`);
    setOpen(false);
  };

  const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleDateString() : '—';

  return (
    <div className="notif-wrapper" ref={panelRef}>
      <button className="notif-bell" onClick={() => setOpen(o => !o)} title="Notifications">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {inReview.length > 0 && (
          <span className="notif-badge">{inReview.length}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-header">
            <span className="notif-title">Notifications</span>
            <button className="notif-refresh" onClick={load} title="Refresh">↻</button>
          </div>

          <div className="notif-tabs">
            <button
              className={`notif-tab ${tab === 'in_review' ? 'active' : ''}`}
              onClick={() => setTab('in_review')}
            >
              In Review {inReview.length > 0 && <span className="notif-tab-count">{inReview.length}</span>}
            </button>
            <button
              className={`notif-tab ${tab === 'completed' ? 'active' : ''}`}
              onClick={() => setTab('completed')}
            >
              Completed {completed.length > 0 && <span className="notif-tab-count">{completed.length}</span>}
            </button>
          </div>

          <div className="notif-body">
            {loading && <div className="notif-empty">Loading...</div>}

            {!loading && tab === 'in_review' && (
              inReview.length === 0
                ? <div className="notif-empty">No pending reviews</div>
                : inReview.map(item => (
                  <div key={item.id} className="notif-item">
                    <div className="notif-item-icon">{item.review_status === 'Changes Requested' ? '↩' : '📋'}</div>
                    <div className="notif-item-body">
                      <div className="notif-item-name">{item.name}</div>
                      <div className="notif-item-path">{item.path}</div>
                      <div className="notif-item-meta">
                        <span className="notif-type-badge">{item.folder_type === 'test_scenario' ? 'Scenarios' : 'Test Cases'}</span>
                        {item.review_status === 'Changes Requested'
                          ? <span className="notif-status-badge changes">↩ Changes Requested</span>
                          : <span>By {item.submitted_by} · {fmt(item.submitted_at)}</span>
                        }
                      </div>
                    </div>
                    <button className="notif-action-btn" onClick={() => goToFolder(item)}>
                      {item.review_status === 'Changes Requested' ? 'Fix →' : 'Review →'}
                    </button>
                  </div>
                ))
            )}

            {!loading && tab === 'completed' && (
              completed.length === 0
                ? <div className="notif-empty">No completed reviews</div>
                : completed.map(item => (
                  <div key={item.id} className="notif-item">
                    <div className="notif-item-icon">
                      {item.review_status === 'Approved' ? '✅' : '↩'}
                    </div>
                    <div className="notif-item-body">
                      <div className="notif-item-name">{item.name}</div>
                      <div className="notif-item-path">{item.path}</div>
                      <div className="notif-item-meta">
                        <span className={`notif-status-badge ${item.review_status === 'Approved' ? 'approved' : 'changes'}`}>
                          {item.review_status}
                        </span>
                        <span>{fmt(item.reviewed_at)}</span>
                      </div>
                    </div>
                    <button className="notif-action-btn secondary" onClick={() => goToFolder(item)}>
                      Open →
                    </button>
                  </div>
                ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
